"use client";
// ===================================================
// מחברת — side list: quick capture, views, filters and the notes themselves.
// Opening a note from here never adds it to the tray; "השאר במגש" does.
// Spec: docs/specs/pinkas-unified-workspace-20260924.md §4
// ===================================================
import { useLayoutEffect, useRef, useState } from "react";
import { Archive, ArchiveRestore, Layers, Pin, Trash2 } from "lucide-react";
import { Button, Input } from "@/components/ui";
import type { PinkasAssignee, PinkasItem, PinkasKind, PinkasPatch } from "@/lib/cc-pinkas-queries";
import type { AssigneeFilter, ListSort, ListView } from "@/lib/pinkas/workspace";
import { snippet } from "@/lib/pinkas/workspace";
import {
  ASSIGNEES,
  KINDS,
  KIND_META,
  SORT_OPTIONS,
  STATUS_LABEL,
  STATUS_NEXT,
  STATUS_TOKEN,
  noteColorHex,
} from "@/lib/pinkas/vocab";
import { IconAction, Seg } from "./PinkasControls";

const VIEW_OPTIONS: { v: ListView; label: string }[] = [
  { v: "active", label: "פעילים" },
  { v: "pinned", label: "נעוצים" },
  { v: "archived", label: "ארכיון" },
];

export interface QuickAddInput {
  raw: string;
  kind: PinkasKind;
  assignee: PinkasAssignee;
}

export function PinkasNoteList({
  items,
  loading,
  activeId,
  tray,
  view,
  onView,
  filter,
  onFilter,
  sort,
  onSort,
  searching,
  claudeQueue,
  newCount,
  isNew,
  onOpen,
  onToggleTray,
  onPatch,
  onDelete,
  onQuickAdd,
  scrollTop,
  onScrollTop,
}: {
  items: PinkasItem[];
  loading: boolean;
  activeId: string | null;
  tray: ReadonlySet<string>;
  view: ListView;
  onView: (v: ListView) => void;
  filter: AssigneeFilter;
  onFilter: (f: AssigneeFilter) => void;
  sort: ListSort;
  onSort: (s: ListSort) => void;
  searching: boolean;
  claudeQueue: number;
  newCount: number;
  isNew: (it: PinkasItem) => boolean;
  onOpen: (id: string) => void;
  onToggleTray: (id: string) => void;
  onPatch: (id: string, patch: PinkasPatch) => void;
  onDelete: (item: PinkasItem) => void;
  /** Resolves only after the row exists; rejects to keep the typed text. */
  onQuickAdd: (input: QuickAddInput) => Promise<void>;
  scrollTop: number;
  onScrollTop: (top: number) => void;
}) {
  const [raw, setRaw] = useState("");
  const [kind, setKind] = useState<PinkasKind>("note");
  const [assignee, setAssignee] = useState<PinkasAssignee>("both");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const scroller = useRef<HTMLDivElement>(null);
  const restored = useRef(false);

  // Restore the list's scroll position once, after the first rows render.
  useLayoutEffect(() => {
    if (restored.current || loading || !scroller.current) return;
    scroller.current.scrollTop = scrollTop;
    restored.current = true;
  }, [loading, scrollTop]);

  async function submit() {
    if (!raw.trim() || adding) return;
    setAdding(true);
    setAddError("");
    try {
      await onQuickAdd({ raw, kind, assignee });
      setRaw(""); // cleared only after the row exists
    } catch (err) {
      setAddError(`היצירה נכשלה — הטקסט נשאר כאן. ${err instanceof Error ? err.message : ""}`.trim());
    } finally {
      setAdding(false);
    }
  }

  const filterOptions = [
    { v: "all" as const, label: "הכל" },
    { v: "gal" as const, label: "לי" },
    { v: "claude" as const, label: claudeQueue > 0 ? `לקלוד · ${claudeQueue}` : "לקלוד" },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* quick capture ---------------------------------------------------- */}
      <div className="space-y-1.5 border-b border-[var(--border-hairline)] p-2">
        <div className="flex items-center gap-1.5">
          <div className="min-w-0 flex-1">
            <Input
              inputSize="sm"
              aria-label="הוספה מהירה"
              value={raw}
              onChange={(e) => {
                setRaw(e.target.value);
                setAddError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void submit();
                }
              }}
              placeholder="הוספה מהירה…  @קלוד · ! דחוף · #תגית"
            />
          </div>
          <Button type="button" size="sm" onClick={() => void submit()} disabled={!raw.trim()} loading={adding}>
            הוסף
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Seg label="סוג לפריט חדש" value={kind} onChange={setKind} options={KINDS} />
          <Seg label="בשביל מי" value={assignee} onChange={setAssignee} options={ASSIGNEES} />
        </div>
        {addError ? (
          <p role="alert" className="text-[11px] text-[var(--role-error)]">
            {addError}
          </p>
        ) : null}
      </div>

      {/* views & filters -------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--border-hairline)] px-2 py-1.5">
        <Seg label="תצוגה" value={view} onChange={onView} options={VIEW_OPTIONS} />
        <Seg label="סינון לפי מי" value={filter} onChange={onFilter} options={filterOptions} />
        <Seg label="מיון" value={sort} onChange={onSort} options={SORT_OPTIONS} />
        {newCount > 0 ? (
          <span className="text-[11px] text-[var(--theme-primary)]">● {newCount} חדש מקלוד</span>
        ) : null}
      </div>

      {/* notes -------------------------------------------------------------- */}
      <div
        ref={scroller}
        onScroll={(e) => onScrollTop(e.currentTarget.scrollTop)}
        className="min-h-0 flex-1 overflow-y-auto p-1.5"
        aria-label="רשימת פתקים"
      >
        {loading ? (
          <p className="p-6 text-center text-sm text-[var(--theme-text-muted)]">טוען מחברת…</p>
        ) : items.length === 0 ? (
          <p className="p-6 text-center text-sm text-[var(--theme-text-faint)]">
            {searching ? "לא נמצאו פתקים לחיפוש הזה" : view === "archived" ? "הארכיון ריק" : view === "pinned" ? "אין פתקים נעוצים" : "אין פתקים עדיין — כתוב משהו למעלה"}
          </p>
        ) : (
          <ul className="space-y-0.5">
            {items.map((it) => (
              <NoteRow
                key={it.id}
                item={it}
                active={it.id === activeId}
                inTray={tray.has(it.id)}
                isNew={isNew(it)}
                onOpen={() => onOpen(it.id)}
                onToggleTray={() => onToggleTray(it.id)}
                onPatch={(p) => onPatch(it.id, p)}
                onDelete={() => onDelete(it)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function NoteRow({
  item,
  active,
  inTray,
  isNew,
  onOpen,
  onToggleTray,
  onPatch,
  onDelete,
}: {
  item: PinkasItem;
  active: boolean;
  inTray: boolean;
  isNew: boolean;
  onOpen: () => void;
  onToggleTray: () => void;
  onPatch: (p: PinkasPatch) => void;
  onDelete: () => void;
}) {
  const excerpt = snippet(item.body, 80);
  return (
    <li
      className={`group relative rounded-lg border ${
        active
          ? "border-[var(--role-selection-border)] bg-[var(--role-selection-bg)]"
          : "border-transparent hover:bg-[var(--role-hover-bg)]"
      } ${item.archived ? "opacity-60" : ""}`}
      style={item.color ? { borderInlineStartColor: noteColorHex(item.color), borderInlineStartWidth: 3 } : undefined}
    >
      <Button
        type="button"
        variant="ghost"
        aria-current={active ? "true" : undefined}
        onClick={onOpen}
        className="w-full flex-col items-stretch text-start"
        style={{ height: "auto", padding: "6px 8px", borderRadius: 8, background: "transparent", gap: 2 }}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <span aria-hidden className="shrink-0 text-sm leading-none" title={KIND_META[item.kind].label}>
            {KIND_META[item.kind].emoji}
          </span>
          <span
            className={`min-w-0 flex-1 truncate text-[13px] ${
              item.status === "done" ? "text-[var(--theme-text-faint)] line-through" : "text-[var(--theme-text)]"
            } ${active ? "font-semibold" : "font-medium"}`}
          >
            {item.text}
          </span>
          {isNew ? <span className="shrink-0 text-[10px] text-[var(--theme-primary)]" title="חדש מקלוד">●</span> : null}
          {item.pinned ? <Pin aria-label="נעוץ" size={12} className="shrink-0 text-[var(--theme-primary)]" /> : null}
          {inTray ? <Layers aria-label="במגש" size={12} className="shrink-0 text-[var(--theme-text-faint)]" /> : null}
        </span>
        {excerpt ? (
          <span className="block truncate text-[11px] font-normal text-[var(--theme-text-faint)]">{excerpt}</span>
        ) : null}
      </Button>

      {/* status + actions — always reachable by keyboard and touch, quiet until hover/focus */}
      <div className="flex items-center gap-0.5 px-1.5 pb-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onPatch({ status: STATUS_NEXT[item.status] })}
          title="לחץ לשינוי סטטוס"
          className="text-[10px] text-[var(--theme-text-muted)]"
          style={{ height: 20, paddingInline: 6, borderRadius: 5, gap: 4 }}
        >
          <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ background: STATUS_TOKEN[item.status] }} />
          {STATUS_LABEL[item.status]}
        </Button>
        {item.priority === "high" ? <span className="text-[10px] text-[var(--role-error)]">דחוף</span> : null}
        <span className="flex-1" />
        <div
          className={`flex items-center gap-0.5 transition-opacity ${
            active ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100"
          }`}
        >
          <IconAction icon={Layers} label={inTray ? "הוצא מהמגש" : "השאר במגש"} active={inTray} onClick={onToggleTray} />
          <IconAction icon={Pin} label={item.pinned ? "בטל נעיצה" : "נעץ"} active={item.pinned} onClick={() => onPatch({ pinned: !item.pinned })} />
          <IconAction
            icon={item.archived ? ArchiveRestore : Archive}
            label={item.archived ? "החזר מארכיון" : "לארכיון"}
            onClick={() => onPatch({ archived: !item.archived })}
          />
          <IconAction icon={Trash2} label="מחק" onClick={onDelete} />
        </div>
      </div>
    </li>
  );
}
